/**
 * Simple Slider Field Renderer
 *
 * A single-value slider with label and current value display
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Simple Slider Renderer
 * Features:
 * - Single value selection
 * - Min/max/step configuration
 * - Current value display
 * - Default value support
 */
export const renderSimpleSlider: FieldRenderer = ({
    field,
    value,
    handleChange,
    isRequired
}) => {
    const {
        minValue = 0,
        maxValue = 100,
        step = 1,
        defaultValue = 50,
    } = field;

    // Initialize slider value - always use array with single value for consistency
    const getInitialValue = () => {
        if (value !== undefined && value !== null) {
            // If value is a number, convert to array
            return [typeof value === 'number' ? value : defaultValue];
        }
        return [defaultValue];
    };

    const sliderValue = getInitialValue();

    const handleSliderChange = (newValue: number[]) => {
        // Store only the first value as a number
        handleChange(field.name, newValue[0]);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <Label className="leading-6">
                    {field.label}
                    {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <output className="text-sm font-medium tabular-nums">{sliderValue[0]}</output>
            </div>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <Slider
                value={sliderValue}
                onValueChange={handleSliderChange}
                min={minValue}
                max={maxValue}
                step={step}
                aria-label={field.label}
            />
        </div>
    );
};

export default renderSimpleSlider;
